import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { omit } from 'remeda';

import { logger } from '../../datadog/logger';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { getVideoPlatform } from '../../hooks/useVideoPlatform';
import { useAppDependenciesContext } from '../../state';
import { appJotaiStore } from '../../state/jotai';
import { Statsig } from '../../statsig/StatsigProvider';
import { telehealthZoomEventLoggingEnabledGate } from '../../statsig/gates';
import { WithRetryOptions, trySafely, withRetry } from '../../utils';
import { JoinRoomOptions, VideoSDKAdapter } from '../core/adapter';
import {
  DEFAULT_VIDEO_SDK_ATOM_VALUE,
  baseVideoSDKAdapterAtom,
  connectedAtAtom,
  hasBackgroundProcessorFailureAtom,
  roomAtom,
  videoSDKAdapterAtom,
  videoSDKAtom,
} from '../core/atoms';
import { useStoredAudioVideoSettings } from '../core/hooks/useStoredAudioVideoSettings';
import { RoomDelegator } from '../core/room/RoomDelegator';
import { participantsAtom } from '../core/room/participant/atoms';
import {
  localAudioContextAtom,
  localMediaStatusesAtom,
  localVideoContextAtom,
} from '../core/room/participant/media/atoms';
import { videoRoomTokenAtom } from '../core/room/state';
import { LocalMediaStatus, VideoSDKAtom } from '../core/types';
import { VideoPlatform, videoPlatformsMatch } from '../videoPlatform';

export type VideoSDKVendor = 'twilio' | 'zoom';
const VIDEO_SDK_INITIALIZATION_RETRY_CONFIG: Readonly<WithRetryOptions> = {
  retries: 3,
  initialDelayMs: 500,
  backoffFactor: 2,
  onFailure({ attempt, isFinalAttempt, error }) {
    if (isFinalAttempt) {
      logger.error(`Video SDK initialization failed after ${attempt} attempts.`, {}, error);
      return;
    }
    logger.warn(`Video SDK initialization attempt ${attempt} failed. Retrying...`, {}, error);
  },
};

/**
 * Returns a callback that lazily loads and initializes the desired video SDK
 * adapter
 */
export function useInitializeVideoSDKAdapter() {
  const setAdapter = useSetAtom(videoSDKAdapterAtom);
  const { useApolloClient } = useAppDependenciesContext();
  const apolloClient = useApolloClient();

  // Using memo instead of callback because callback complains that the
  // function's dependencies are unknown: useCallback(withRetry(...), [...])
  return useMemo(
    () =>
      withRetry(async (vendor: VideoSDKVendor) => {
        switch (vendor) {
          case 'twilio': {
            logger.info('Initializing Twilio Video SDK adapter');
            const { TwilioVideoSDKAdapter } = await import('../twilio/adapter');
            const newAdapter = new TwilioVideoSDKAdapter({
              apolloClient,
              jotaiStore: appJotaiStore,
            });
            setAdapter(newAdapter);
            break;
          }
          case 'zoom': {
            logger.info('Initializing Zoom Video SDK adapter');
            const { interceptUserMedia } = await import('../zoom/utils/interceptUserMedia');
            interceptUserMedia(navigator);
            const { ZoomVideoSDKAdapter } = await import('../zoom/adapter');
            const newAdapter = await ZoomVideoSDKAdapter.create({
              apolloClient,
              jotaiStore: appJotaiStore,
              enableEventLogging: Statsig.checkGate(telehealthZoomEventLoggingEnabledGate),
            });
            setAdapter(newAdapter);
            break;
          }
          default: {
            const _exhaustiveCheck: never = vendor;
            throw new Error(`Unsupported vendor: ${_exhaustiveCheck}`);
          }
        }
      }, VIDEO_SDK_INITIALIZATION_RETRY_CONFIG),
    [setAdapter, apolloClient],
  );
}

export function useConnectAudioVideoInputs() {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const setLocalVideoContext = useSetAtom(localVideoContextAtom);
  const setLocalAudioContext = useSetAtom(localAudioContextAtom);
  const setHasBackgroundProcessorFailure = useSetAtom(hasBackgroundProcessorFailureAtom);
  const getLocalVideoContext = useAtomCallbackMemoized((get) => get(localVideoContextAtom), []);
  const getLocalAudioContext = useAtomCallbackMemoized((get) => get(localAudioContextAtom), []);
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();

  return useCallback(async (): Promise<void> => {
    return await getAdapter().connectAudioVideoInputs({
      setLocalVideoContext,
      setLocalAudioContext,
      getLocalAudioContext,
      getLocalVideoContext,
      setHasBackgroundProcessorFailure,
      setStoredAudioVideoSettings,
    });
  }, [
    getAdapter,
    setLocalVideoContext,
    setLocalAudioContext,
    getLocalAudioContext,
    getLocalVideoContext,
    setHasBackgroundProcessorFailure,
    setStoredAudioVideoSettings,
  ]);
}

/**
 * Returns a best-effort callback that primes audio autoplay within a user gesture.
 */
export function usePrimeAudioAutoPlayAction() {
  // crucial to get the base adapter, which doesn't throw when null
  const getBaseAdapter = useAtomCallbackMemoized((get) => get(baseVideoSDKAdapterAtom), []);

  return useCallback(async (): Promise<void> => {
    const adapter = getBaseAdapter();
    if (!adapter) return;
    await trySafely(adapter.primeAudioAutoPlay(), (error) => {
      logger.warn('Failed to prime audio autoplay', {}, error);
    });
  }, [getBaseAdapter]);
}

export function useJoinRoom() {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const getLocalAudioContext = useAtomCallbackMemoized((get) => get(localAudioContextAtom), []);
  const getLocalVideoContext = useAtomCallbackMemoized((get) => get(localVideoContextAtom), []);

  const setRoom = useSetAtom(roomAtom);
  const setParticipants = useSetAtom(participantsAtom);
  const setConnectedAt = useSetAtom(connectedAtAtom);

  return useCallback(
    async (token: string, displayName: string, options?: JoinRoomOptions): Promise<RoomDelegator> => {
      const room = await getAdapter().joinRoom(
        token,
        displayName,
        getLocalAudioContext(),
        getLocalVideoContext(),
        options,
      );

      setRoom(room);
      setParticipants(Array.from(room.remoteParticipants, ([_, p]) => p));
      setConnectedAt(Date.now());

      return room;
    },
    [getLocalAudioContext, getLocalVideoContext, getAdapter, setRoom, setParticipants, setConnectedAt],
  );
}

export function useLeaveRoom(): VideoSDKAdapter['leaveRoom'] {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);

  return useCallback(
    async (...args) => {
      return await getAdapter().leaveRoom(...args);
    },
    [getAdapter],
  );
}

export function useRejoinRoom() {
  const joinRoom = useJoinRoom();
  const getRoomToken = useAtomCallbackMemoized((get) => get(videoRoomTokenAtom), []);
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);

  return useCallback(
    async (options: JoinRoomOptions) => {
      const roomToken = getRoomToken();
      if (!roomToken) {
        throw new Error('No room token available for rejoining the room');
      }

      return await joinRoom(roomToken, getAdapter().displayName, options);
    },
    [getAdapter, getRoomToken, joinRoom],
  );
}

/**
 * Switches the video platform in-place (no page reload). Initializes the new
 * adapter before tearing down the old one, ensuring the atom is never null and
 * subscribed components don't throw.
 */
export function useCoerceVideoPlatform({
  useInitializeVideoSDKAdapter: useInitializeVideoSDKAdapter_ = useInitializeVideoSDKAdapter,
  useConnectAudioVideoInputs: useConnectAudioVideoInputs_ = useConnectAudioVideoInputs,
}: {
  useInitializeVideoSDKAdapter?: typeof useInitializeVideoSDKAdapter;
  useConnectAudioVideoInputs?: typeof useConnectAudioVideoInputs;
} = {}) {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const getVideoSDKAtom = useAtomCallbackMemoized((get) => get(videoSDKAtom), []);
  const setVideoSDK = useSetAtom(videoSDKAtom);
  const initializeVideoSDKAdapter = useInitializeVideoSDKAdapter_();
  const connectAudioVideoInputs = useConnectAudioVideoInputs_();
  const setLocalMediaStatuses = useSetAtom(localMediaStatusesAtom);
  const { logger } = useAppDependenciesContext();

  return useCallback(
    async (
      targetPlatform: VideoPlatform,
      {
        preservedSDKFields = [
          'availableDevices',
          'roomToken',
          'thumbnailPosition',
          'twilioIdentity',
          'usedTwilioWebsocketsSuccessfully',
          'usingWebsocketsReverseProxy',
          'videoQuality',
          'zoomToken',
        ],
      }: {
        /**
         * {@link VideoSDKAtom} fields to preserve during coercion; all other
         * {@link VideoSDKAtom} fields are set to their default values.
         */
        preservedSDKFields?: (keyof VideoSDKAtom)[];
      } = {},
    ) => {
      const previousVideoSDK = getVideoSDKAtom();
      const adapter = getAdapter();

      if (videoPlatformsMatch(getVideoPlatform(adapter), targetPlatform)) {
        return;
      }

      setVideoSDK((prev) => ({
        ...prev,
        ...omit(DEFAULT_VIDEO_SDK_ATOM_VALUE, preservedSDKFields),
        localAudioContext: { status: LocalMediaStatus.LOADING },
        localVideoContext: { status: LocalMediaStatus.LOADING },
      }));

      try {
        await initializeVideoSDKAdapter(targetPlatform);
        await trySafely(adapter.tearDown(), (error) => {
          logger.error(
            'Failed to tear down previous adapter',
            {
              fromPlatform: getVideoPlatform(adapter),
              toPlatform: targetPlatform,
            },
            error,
          );
        });
        await trySafely(connectAudioVideoInputs());
      } finally {
        setLocalMediaStatuses((current) => {
          const { audio: currentAudioStatus, video: currentVideoStatus } = current;
          const {
            localAudioContext: { status: previousAudioStatus },
            localVideoContext: { status: previousVideoStatus },
          } = previousVideoSDK;

          return {
            audio: currentAudioStatus === LocalMediaStatus.LOADING ? previousAudioStatus : currentAudioStatus,
            video: currentVideoStatus === LocalMediaStatus.LOADING ? previousVideoStatus : currentVideoStatus,
          };
        });
      }
    },
    [
      getVideoSDKAtom,
      getAdapter,
      setVideoSDK,
      initializeVideoSDKAdapter,
      connectAudioVideoInputs,
      logger,
      setLocalMediaStatuses,
    ],
  );
}
